import { LocalDate } from '@payer-admin-ui/api/src/external/java/LocalDate';
import { CurrencyCode } from '@payer-admin-ui/api/src/invoices/models/CurrencyCode';
import { ExternalOrderStatus } from '@payer-admin-ui/api/src/invoices/models/ExternalOrderStatus';
import { InvoiceStatus } from '@payer-admin-ui/api/src/invoices/models/InvoiceStatus';
import { InvoiceType } from '@payer-admin-ui/api/src/invoices/models/InvoiceType';
import { PaymentStatus } from '@payer-admin-ui/api/src/invoices/models/PaymentStatus';
import { createFilterAdapter } from '@payer-admin-ui/components-v2/src/state/filterAdapter';
import { createPaginationAdapter } from '@payer-admin-ui/components-v2/src/state/paginationAdapter';
import { createMultiColumnSortingAdapter } from '@payer-admin-ui/components-v2/src/state/sortingAdapter';
import {
  AppEntityState,
  FetchingEntityState,
  InvoiceNumberObject,
  ListViewWithFilterAndMultiSortState,
  SingleSortType,
  SingleViewState,
} from '@payer-admin-ui/components-v2/src/types/state';
import { createAsyncThunk, createEntityAdapter, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { availablePageSizes } from 'config';
import { formatISO, startOfMonth, subMonths } from 'date-fns';
import { chain, difference, map } from 'lodash';
import {
  InternalInvoiceResponseV3,
  InvoicePageResponse,
  Summary,
} from '@payer-admin-ui/api/src/invoices/responses/InvoiceResponseV3';
import { invoiceAPI } from '../../../redux/invoiceAPI';
import { MerchantAdminState } from '../../../redux/store';
import { updateTableSettings, clearTableSettings } from '../../../redux/userSettings';

export const INVOICES_TABLE_SETTINGS_KEY = 'invoices';

export const fetchInvoice = createAsyncThunk<InternalInvoiceResponseV3, InvoiceNumberObject>(
  'invoices/fetchByInvoiceNumber',
  async ({ invoiceNumber }) => {
    return await invoiceAPI.fetchByInvoiceNumber({ invoiceNumber });
  },
);

export const fetchInvoices = createAsyncThunk<InvoicePageResponse, void, { state: MerchantAdminState }>(
  'invoices/fetchPage',
  async (_, { getState }) => {
    const { page, pageSize, appliedFilters, filterOverride, sortState } = getState().invoices.global.listView;
    const sort = (sortState || [])
      .filter(s => s.sortDirection)
      .map(s => `${s.column}|${s.sortDirection.toUpperCase()}`);

    const effectiveFilters = filterOverride ?? appliedFilters;

    return invoiceAPI.fetchPage({ page, pageSize, filters: effectiveFilters, sort });
  },
);

export const applyInvoiceFilters = createAsyncThunk<void, void, { state: MerchantAdminState }>(
  'invoices/applyInvoiceFilters',
  async (_, { getState, dispatch }) => {
    dispatch(applyFilters());
    dispatch(
      updateTableSettings({
        component: INVOICES_TABLE_SETTINGS_KEY,
        update: { appliedFilters: getState().invoices.global.listView.appliedFilters },
      }),
    );
  },
);

export const setInvoiceSort = createAsyncThunk<
  void,
  SingleSortType<InvoicesSortableColumn>,
  { state: MerchantAdminState }
>('invoices/setInvoiceSort', async (sort, { getState, dispatch }) => {
  dispatch(setSort(sort));
  const sortModel = getState().invoices.global.listView.sortState;
  dispatch(
    updateTableSettings({
      component: INVOICES_TABLE_SETTINGS_KEY,
      update: { sortOrders: sortModel },
    }),
  );
  dispatch(fetchInvoices());
});

export const syncUserSettings = createAsyncThunk<void, void, { state: MerchantAdminState }>(
  'invoices/syncUserSettings',
  async (_, { getState, dispatch }) => {
    const settings = getState().userSettings?.tableSettings?.[INVOICES_TABLE_SETTINGS_KEY];
    const pageSize = settings?.pageSize;
    const sortModel = settings?.sortOrders;
    const appliedFilters = settings?.appliedFilters;
    if (pageSize) {
      dispatch(setPageSize(pageSize));
    }
    if (sortModel) {
      dispatch(setSorts(sortModel as SingleSortType<InvoicesSortableColumn>[]));
    }
    dispatch(
      setFilter({
        ...getState().invoices.global.listView.filters,
        ...appliedFilters,
      }),
    );
    dispatch(applyFilters());
    dispatch(setInited());
  },
);

export const clearInvoicesTableSettings = createAsyncThunk<void, void, { state: MerchantAdminState }>(
  'invoices/clearTableSettings',
  async (_, { dispatch }) => {
    dispatch(setFilter(initialFilters));
    dispatch(applyFilters());
    dispatch(setSorts([]));
    dispatch(setPageSize(availablePageSizes[0]));
    dispatch(
      clearTableSettings({
        component: INVOICES_TABLE_SETTINGS_KEY,
        customDefaultSettings: {
          appliedFilters: initialFilters,
        },
      }),
    );
  },
);

const SINGLE_CACHE_SIZE = 10;

export enum InvoicesSortableColumn {
  invoiceNumber = 'invoiceNumber',
  externalInvoiceNumber = 'externalInvoiceNumber',
  customerNumber = 'customerNumber',
  companyName = 'companyName',
  invoiceDate = 'invoiceDate',
  dueDate = 'dueDate',
  currencyCode = 'currencyCode',
  type = 'type',
  deliveryType = 'deliveryType',
  amountToPay = 'amountToPay',
  status = 'status',
  debtCollection = 'debtCollection',
}

export type InvoiceFilters = {
  invoiceType: InvoiceType[];
  invoiceStatus: InvoiceStatus[];
  currencyCode: CurrencyCode[];
  paymentStatus: (PaymentStatus | 'OVERDUE')[];
  orderStatus: ExternalOrderStatus[];
  invoiceDateStart: LocalDate;
  invoiceDateEnd: LocalDate;
  dueDateBefore: LocalDate;
  amountStart: string | null;
  amountEnd: string | null;
  orderApprovalDateStart: LocalDate;
  orderApprovalDateEnd: LocalDate;
  companyName: string | null;
  regNumber: string | null;
  site: string | null;
  partOfInvoiceAggregation: boolean | null;
  search: string;
};

export type InvoicesGlobalState = AppEntityState<InternalInvoiceResponseV3, number> &
  SingleViewState<number> &
  ListViewWithFilterAndMultiSortState<number, InvoiceFilters, InvoicesSortableColumn> & {
    listView: {
      summary?: Partial<Record<CurrencyCode, Summary>>;
      inited: boolean;
      filterOverride: InvoiceFilters | null;
    };
  };

export type InvoiceEntity = FetchingEntityState<InternalInvoiceResponseV3, number>;

export const emptyInvoiceFilters: InvoiceFilters = {
  invoiceType: [],
  invoiceStatus: [],
  currencyCode: [],
  paymentStatus: [],
  orderStatus: [],
  invoiceDateStart: '',
  invoiceDateEnd: '',
  dueDateBefore: '',
  amountStart: null,
  amountEnd: null,
  orderApprovalDateStart: '',
  orderApprovalDateEnd: '',
  companyName: null,
  regNumber: null,
  site: null,
  partOfInvoiceAggregation: null,
  search: '',
};

const entityAdapter = createEntityAdapter<InvoiceEntity>();
const paginationAdapter = createPaginationAdapter<InvoicesGlobalState>({
  selectState: state => state.listView,
});

const filterAdapter = createFilterAdapter<InvoicesGlobalState, InvoiceFilters>({
  selectState: state => state.listView,
  emptyFilters: emptyInvoiceFilters,
  reset: paginationAdapter.reset,
});

const initialFilters = {
  ...emptyInvoiceFilters,
  invoiceStatus: [InvoiceStatus.NEW, InvoiceStatus.FINAL, InvoiceStatus.DRAFT],
  invoiceDateStart: formatISO(startOfMonth(subMonths(new Date(), 6)), { representation: 'date' }), // workaround slow database query causing long initial load times
};

const sortingAdapter = createMultiColumnSortingAdapter<InvoicesGlobalState, InvoicesSortableColumn>({
  selectState: state => state.listView,
  reset: paginationAdapter.reset,
});

const initialState: InvoicesGlobalState = entityAdapter.getInitialState({
  singleView: {
    cachedIds: [],
  },
  listView: {
    isFetching: false,
    error: null,
    inited: false,
    totalCount: null,
    ids: [],
    summary: undefined,
    page: 0, // zero-indexed in state, one-indexed in UI
    pageSize: availablePageSizes[0],
    filters: initialFilters,
    appliedFilters: initialFilters,
    sortState: undefined,
    filterOverride: null,
  },
});

const pruneCache = (state: InvoicesGlobalState) => {
  entityAdapter.removeMany(state, difference(state.ids, state.singleView.cachedIds, state.listView.ids));
};

const upsertOnePruneCache = (state: InvoicesGlobalState, entity: InvoiceEntity) => {
  entityAdapter.upsertOne(state, entity);
  state.singleView.cachedIds = chain([entity.id]).union(state.singleView.cachedIds).take(SINGLE_CACHE_SIZE).value();
  pruneCache(state);
};

const invoicesSlice = createSlice({
  name: 'invoices/global',
  initialState,
  reducers: {
    resetError: state => {
      state.listView.error = null;
    },
    upsertInvoice: (state, { payload }: PayloadAction<InternalInvoiceResponseV3>) => {
      upsertOnePruneCache(state, { id: payload.invoiceNumber, entity: payload, isFetching: false, error: null });
    },
    upsertInvoiceInListView: (state, { payload }: PayloadAction<InternalInvoiceResponseV3>) => {
      upsertOnePruneCache(state, { id: payload.invoiceNumber, entity: payload, isFetching: false, error: null });

      const effectiveFilters = state.listView.filterOverride ?? state.listView.appliedFilters;

      if (effectiveFilters.invoiceType.length && !effectiveFilters.invoiceType.includes(payload.type)) return;
      if (effectiveFilters.invoiceStatus.length && !effectiveFilters.invoiceStatus.includes(payload.status)) return;
      if (effectiveFilters.currencyCode.length && !effectiveFilters.currencyCode.includes(payload.currencyCode)) return;
      if (
        effectiveFilters.orderStatus.length &&
        (!payload.orderStatus || !effectiveFilters.orderStatus.includes(payload.orderStatus))
      ) {
        return;
      }

      if (effectiveFilters.paymentStatus.length) {
        const paymentStatusFilters = effectiveFilters.paymentStatus;
        const includesExactStatus = paymentStatusFilters.includes(payload.paymentStatus);
        const includesOverdue = paymentStatusFilters.includes('OVERDUE');
        if (!includesExactStatus && !(includesOverdue && payload.isOverdue)) return;
      }

      if (state.listView.ids.includes(payload.invoiceNumber)) return;

      state.listView.ids = [payload.invoiceNumber, ...state.listView.ids].slice(0, state.listView.pageSize);
      if (state.listView.totalCount !== null) state.listView.totalCount += 1;
    },
    resetInvoiceError: (state, { payload }: PayloadAction<number>) => {
      const entity = state.entities[payload];
      if (!entity) return;
      entity.error = null;
    },
    setInited: state => {
      state.listView.inited = true;
    },
    setFilterOverride: (state, { payload }: PayloadAction<InvoiceFilters>) => {
      state.listView.filterOverride = payload;
      state.listView.page = 0;
    },
    clearFilterOverride: state => {
      state.listView.filterOverride = null;
    },
    setPage: paginationAdapter.setPage,
    setPageSize: paginationAdapter.setPageSize,
    setFilter: filterAdapter.setFilter,
    clearAllFilters: filterAdapter.clearAllFilters,
    clearFilter: filterAdapter.clearFilter,
    applyFilters: filterAdapter.applyFilters,
    setSort: sortingAdapter.setSort,
    setSorts: sortingAdapter.setSorts,
    resetSort: sortingAdapter.resetSort,
  },
  extraReducers: builder => {
    builder.addCase(fetchInvoice.pending, (state, { meta: { arg } }) => {
      upsertOnePruneCache(state, { id: arg.invoiceNumber, isFetching: true, error: null });
    });
    builder.addCase(fetchInvoice.fulfilled, (state, { meta: { arg }, payload: invoice }) => {
      entityAdapter.upsertOne(state, { id: arg.invoiceNumber, entity: invoice, isFetching: false, error: null });
    });
    builder.addCase(fetchInvoice.rejected, (state, { meta: { arg }, error }) => {
      entityAdapter.upsertOne(state, { id: arg.invoiceNumber, entity: undefined, isFetching: false, error });
    });

    builder.addCase(fetchInvoices.pending, state => {
      state.listView = {
        ...state.listView,
        isFetching: true,
        error: null,
      };
    });
    builder.addCase(fetchInvoices.fulfilled, (state, { payload: { contents, totalCount, summary } }) => {
      const invoices = normalize(contents);
      state.listView = {
        ...state.listView,
        ids: map(invoices, 'id'),
        totalCount,
        isFetching: false,
        summary,
      };
      entityAdapter.upsertMany(state, invoices);
      pruneCache(state);
    });
    builder.addCase(fetchInvoices.rejected, (state, { error }) => {
      state.listView = {
        ...state.listView,
        ids: [],
        totalCount: null,
        isFetching: false,
        error,
        summary: undefined,
      };
      pruneCache(state);
    });
  },
});

const normalize = (invoices: InternalInvoiceResponseV3[]): InvoiceEntity[] =>
  map(invoices, invoice => ({ id: invoice.invoiceNumber, entity: invoice, isFetching: false, error: null }));

export const {
  actions: {
    resetError,
    upsertInvoice,
    upsertInvoiceInListView,
    resetInvoiceError,
    setPage,
    setPageSize,
    setFilter,
    clearAllFilters,
    clearFilter,
    applyFilters,
    setSort,
    setSorts,
    resetSort,
    setInited,
    setFilterOverride,
    clearFilterOverride,
  },
  reducer: invoicesReducer,
} = invoicesSlice;

export const invoicesSelectors = entityAdapter.getSelectors<MerchantAdminState>(state => state.invoices.global);
